import { useEffect, useState } from "react"

import { Flex, Spin, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import InputNumberComponent from "../../../../../Components/Input/InputNumber"
import SelectComponent from "../../../../../Components/Select"
import { PRICING_TAB } from "../../../../PricingPage/redux/pricing.constants"
import {
  selectLookupAssignmentOptions,
  selectLookupAssignmentOptionsFetching
} from "../../../../PricingPage/redux/pricing.slice"
import { getLookupPricingAssignmentOptions } from "../../../../PricingPage/redux/pricing.thunks"
import { updateUserLookupPrice } from "../../../../User/redux/user.thunks"

import styles from "../../styles.module.less"

const { Text } = Typography

const PRICE_ROWS = [
  { field: "banking_app_price", label: "Banking app price ($)" },
  { field: "call_center_price", label: "Call Center price ($)" },
  { field: "mastercom_price", label: "Mastercom price ($)" }
]

// The unsaved selection is local state, so leaving the tab (which unmounts it) discards it.
const LookupsTab = () => {
  const dispatch = useDispatch()

  const businessAccount = useSelector((state) => state.user.businessAccount)
  const saveFetching = useSelector((state) => state.user.saveFetching)
  const schemes = useSelector(selectLookupAssignmentOptions)
  const fetching = useSelector(selectLookupAssignmentOptionsFetching)

  const savedId = businessAccount?.lookup_pricing ?? null
  const [selectedId, setSelectedId] = useState(savedId)

  useEffect(() => {
    dispatch(getLookupPricingAssignmentOptions())
  }, [dispatch])

  // The saved scheme is the baseline: another account or a successful save resets the selection to it.
  useEffect(() => {
    setSelectedId(savedId)
  }, [businessAccount?.id, savedId])

  const selected = schemes.find(({ id }) => id === selectedId)

  const options = schemes.map(({ id, name }) => ({
    value: id,
    label: name,
    "data-testid": `business-pricing-scheme-option-${id}`
  }))

  // No account loaded (its fetch failed): nothing to save the scheme on.
  const canSave = Boolean(businessAccount?.id && selected && selectedId !== savedId)

  const handleSave = () => {
    if (!canSave) return

    dispatch(updateUserLookupPrice({ ba_id: businessAccount.id, lookup_pricing: selectedId }))
  }

  return (
    <Spin size="large" spinning={fetching || saveFetching}>
      {/* A data-testid on the Spin itself would also land on its always-mounted wrapper. */}
      {(fetching || saveFetching) && <span hidden data-testid="business-pricing-loading" />}
      <Flex vertical={true} gap={30} className={styles.form}>
        <Flex gap={24} className={styles.selectRow}>
          <SelectComponent
            value={selected ? selectedId : null}
            placeholder="Select a lookup pricing scheme"
            onChange={setSelectedId}
            options={options}
            className={"like-input"}
            style={{ width: "100%" }}
            data-testid="business-pricing-scheme-select"
            dropdownRender={(menu) => (
              <div data-testid="business-pricing-scheme-dropdown">{menu}</div>
            )}
          />

          <ButtonComponent
            data-testid="business-pricing-edit-link"
            href={`/pricing?tab=${PRICING_TAB.LOOKUPS}`}
          >
            Edit
          </ButtonComponent>
        </Flex>

        <Flex gap={24} align="center">
          <Text className={styles.label} data-testid="business-pricing-row-free_count-label">
            Number of the first lookups to skip
          </Text>
          <InputNumberComponent
            size="small"
            data-testid="business-pricing-row-free_count-input"
            value={selected?.free_count}
            disabled={true}
            className={styles.value}
          />
        </Flex>

        {PRICE_ROWS.map(({ field, label }) => (
          <Flex gap={24} align="center" key={field}>
            <Text className={styles.label} data-testid={`business-pricing-row-${field}-label`}>
              {label}
            </Text>
            <InputNumberComponent
              size="small"
              data-testid={`business-pricing-row-${field}-input`}
              precision={2}
              value={selected?.[field]}
              disabled={true}
              className={styles.value}
            />
          </Flex>
        ))}
      </Flex>

      <Flex gap={16} justify="end" className={styles.actions}>
        <ButtonComponent
          size="large"
          type="primary"
          disabled={!canSave}
          onClick={handleSave}
          data-testid="business-pricing-save-button"
        >
          Save and apply pricing
        </ButtonComponent>
      </Flex>
    </Spin>
  )
}

export default LookupsTab
